type AsyncFunction<TArgs extends readonly unknown[], TReturn> = (...args: TArgs) => Promise<TReturn>;

type Options<TArgs extends readonly unknown[]> = {
	cacheKey?: (...args: TArgs) => string;
};

type PMemoPublicApi = {
	clearCache: () => void;
};

const DEFAULT_CACHE_KEY = <TArgs extends readonly unknown[]>(...args: TArgs) => String(args[0]);

export function promiseMemo<TArgs extends readonly unknown[], TReturn>(
	callback: AsyncFunction<TArgs, TReturn>,
	options: Options<TArgs> = {}
): AsyncFunction<TArgs, TReturn> & PMemoPublicApi {
	const { cacheKey = DEFAULT_CACHE_KEY } = options;
	const cache: Map<string, Promise<TReturn>> = new Map();

	const promiseMemoWrapper = (...args: TArgs): Promise<TReturn> => {
		const key = cacheKey(...args);

		if (!cache.has(key)) {
			const promise = callback(...args);
			promise.catch(() => {
				// Clear cache entry if promise fails to allow retries
				if (cache.get(key) === promise) {
					cache.delete(key);
				}
			});
			cache.set(key, promise);
		}

		return cache.get(key);
	};

	promiseMemoWrapper.clearCache = () => {
		cache.clear();
	};

	return promiseMemoWrapper;
}

export function promiseMemoOne<TArgs extends readonly unknown[], TReturn>(
	callback: AsyncFunction<TArgs, TReturn>,
	options: Options<TArgs> = {}
): AsyncFunction<TArgs, TReturn> & PMemoPublicApi {
	const { cacheKey = () => '_key_' } = options;

	let cache: Promise<TReturn> | null = null;
	let prevKey: string | undefined;

	const promiseMemoWrapper = (...args: TArgs): Promise<TReturn> => {
		const currentKey = cacheKey(...args);

		if (currentKey !== prevKey || cache === null) {
			const promise = callback(...args);
			promise.catch(() => {
				// Clear cache if promise fails to allow retries
				// Only clear if this promise is still the cached one
				if (cache === promise) {
					cache = null;
					prevKey = undefined;
				}
			});
			cache = promise;
			prevKey = currentKey;
		}

		return cache;
	};

	promiseMemoWrapper.clearCache = () => {
		cache = null;
		prevKey = undefined;
	};

	return promiseMemoWrapper;
}
